import {
  Box,
  Typography,
  Button,
  Divider,
  styled,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import MapContainer from '@components/map/MapContainer'
import WeeklyConsumptionChart from '@/components/WeeklyConsumptionChart/WeeklyConsumptionChart'
import MeterInfoCard from '@/components/ConsumerDetailsCards/MeterInfoCard'
import PersonalInfoCard from '@/components/ConsumerDetailsCards/PersonalInfoCard'
import TransmitterInfoCard from '@/components/ConsumerDetailsCards/TransmitterInfoCard'
import type { LocationOption } from '@/components/ConsumerDetailsCards/DetailRows'
import EditOutlinedIcon from '@assets/ConsumerDetails/Pen.svg'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import TableConnect from '@assets/ConsumerDetails/TableConnect.svg'
import {
  fetchLocations,
  fetchSingleConsumer,
  getConnectedMeters,
  updateConsumer,
  CONSUMER_FETCH_FAILED,
  CONSUMER_SAVE_FAILED,
  LOCATIONS_FETCH_FAILED,
  ConsumerData,
  type ConsumerFetchResult,
  ConsumerRow,
  collectResourceHeaders,
} from '@/services/consumerService'
import PageStateMessage from '@/components/common/PageStateMessage'
import { describeGatewayError, type GatewayError } from '@/utils/gatewayError'
import { getConnectedMetersState } from '@/utils/connectedMetersState'
import {
  changedSaveFields,
  saveFieldError,
  saveRefusalText,
} from '@/utils/consumerSaveFields'
import { getTablePageSize } from '@/hooks/useTablePageSize'
import { buildConsumerColumns } from '@/utils/consumerColumns'
import { useCallback, useEffect, useState, useMemo } from 'react'
import { useAppTheme } from '@/theme/ThemeContext'
import { useOutletContext, useParams } from 'react-router-dom'
import { renderAlertWithMutedIndication } from '@/utils/mutedAlertRenderer'
import { exportConsumerDetailsToPDF, exportConsumerDetailsToExcel } from '@/utils/exportUtils'
import { firstFiniteConsumption } from '@/utils/consumptionFormat'

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#003F61',
  textAlign: 'right',
  fontFamily: 'Rubik',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
}))

// Helper to parse coordinates for the map. Returns null when the meter has no
// stored location: a fallback city centre would put the marker on a street the
// meter is not in, which reads as a located meter.
const parseCoordinates = (
  coordinates: any
): { latitude: number; longitude: number } | null => {
  if (!coordinates) return null

  if (typeof coordinates === 'object') {
    // Case 1: {first, second}
    if (coordinates.first && coordinates.second) {
      return {
        latitude: Number(coordinates.first),
        longitude: Number(coordinates.second),
      }
    }
    // Case 2: {lat, lan} (API format)
    if (coordinates.lat != null && coordinates.lan != null) {
      return {
        latitude: Number(coordinates.lan),
        longitude: Number(coordinates.lat),
      }
    }
    // Case 3: {lat, lng}
    if (coordinates.lat != null && coordinates.lng != null) {
      return {
        latitude: Number(coordinates.lat),
        longitude: Number(coordinates.lng),
      }
    }
    // Case 4: {latitude, longitude}
    if (coordinates.latitude != null && coordinates.longitude != null) {
      return {
        latitude: Number(coordinates.latitude),
        longitude: Number(coordinates.longitude),
      }
    }
  }

  // Case 5: String "lat,lng"
  if (typeof coordinates === 'string') {
    const parts = coordinates.split(',')
    if (parts.length === 2) {
      const lat = Number(parts[0].trim())
      const lng = Number(parts[1].trim())
      if (!isNaN(lat) && !isNaN(lng)) {
        return { latitude: lat, longitude: lng }
      }
    }
  }

  return null
}

import { buildConsumerDetailsClipboardText } from '@/utils/consumerDetailCards'

const ConsumerDetailsOfMeter: React.FC = () => {
  const { setExcelExportHandler, setCopyHandler, setPdfExportHandler, showConsumer } = useOutletContext<{
    setExcelExportHandler?: (fn: (() => void) | null) => void
    setCopyHandler?: (fn: (() => void) | null) => void
    setPdfExportHandler?: (fn: (() => void) | null) => void
    showConsumer?: (result: ConsumerFetchResult) => void
  }>() ?? {}
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const RUBIK =
    '"Rubik", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'
  const BORDER = isDarkMode ? '#374151' : '#E4E7EB'

  const cardBoxStyle = {
    bgcolor: isDarkMode ? '#202B40' : '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    height: '100%',
  }

  const [details, setDetails] = useState<ConsumerData | null>(null)
  const [loadError, setLoadError] = useState<GatewayError | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  // Extract resource headers for localization
  const resourceHeaders = useMemo(() => {
    return details ? collectResourceHeaders([details]) : {}
  }, [details])

  // Connected Meters State
  const [connectedRows, setConnectedRows] = useState<ConsumerRow[]>([])
  const [connectedTotal, setConnectedTotal] = useState(0)
  const [connectedPage, setConnectedPage] = useState(0)
  const [connectedPageSize, setConnectedPageSize] = useState(() =>
    getTablePageSize(10)
  )
  const [isConnectedLoading, setIsConnectedLoading] = useState(false)
  const [connectedError, setConnectedError] = useState<GatewayError | null>(
    null
  )
  const [connectedResourceNames, setConnectedResourceNames] = useState<
    string[]
  >([])
  const [connectedResourceHeaders, setConnectedResourceHeaders] = useState<
    Record<string, string>
  >({})

  // Edit Mode State
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedData, setEditedData] = useState<Record<string, any>>({})
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  const [isLoading, setIsLoading] = useState(true)
  const consumerId = useParams().consumerId

  // The edit state holds only what the user typed; a row without an entry shows the loaded value.
  useEffect(() => {
    setEditedData({})
  }, [details])

  // The site's areas, read once when the user starts to edit.
  const [locations, setLocations] = useState<LocationOption[]>([])
  const [locationsError, setLocationsError] = useState<GatewayError | null>(
    null
  )
  const [locationsToken, setLocationsToken] = useState(0)
  useEffect(() => {
    if (!isEditMode || locations.length > 0) return
    const accountId = localStorage.getItem('accountId')
    if (!accountId) return
    fetchLocations(accountId).then(result => {
      setLocationsError(result.ok ? null : result.error)
      if (result.ok) setLocations(result.data)
    })
  }, [isEditMode, locations.length, locationsToken])

  const meterCoordinates = useMemo(
    () => parseCoordinates(details?.coordinates),
    [details?.coordinates]
  )

  const connectedState = useMemo(
    () =>
      getConnectedMetersState({
        isLoading: isConnectedLoading,
        error: connectedError,
        rowCount: connectedRows.length,
      }),
    [isConnectedLoading, connectedError, connectedRows.length]
  )

  // Memoize chart data so it stays stable during edit mode
  const chartData = useMemo(() => {
    const consumptionData = details?.apiDailyConsumptions
    if (consumptionData?.length) {
      return [...consumptionData]
        .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())
        .map((item: any) => ({
          date: item.day,
          day:
            firstFiniteConsumption(
              item.dailyConsumption,
              item.consumption
            ) ?? 0,
          night:
            firstFiniteConsumption(
              item.nightlyConsumption,
              item.nightConsumption
            ) ?? 0,
        }))
    }
    const fallback = details?.apiConsumerConsumptions
    if (fallback?.length) {
      return [...fallback]
        .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())
        .map((item: any) => ({
          date: item.day,
          day: firstFiniteConsumption(item.consumption) ?? 0,
          night: 0,
        }))
    }
    return []
  }, [details])

  // Handle field changes in edit mode
  const handleFieldChange = (field: string, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }))
  }

  const hasUnsavedChanges = useMemo(() => {
    if (!details) return false
    return Object.keys(changedSaveFields(details, editedData)).length > 0
  }, [details, editedData])

  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true)
      return
    }

    handleConfirmCancel()
  }

  // Cancel editing and restore the original data.
  const handleConfirmCancel = () => {
    setEditedData({})
    setSaveError(null)
    setIsEditMode(false)
    setShowCancelDialog(false)
  }

  const handleContinueEditing = () => {
    setShowCancelDialog(false)
  }

  // State for save operation
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const { langKey } = useAppTheme()

  // Build dynamic columns for connected meters table (same approach as main consumers table)
  // Then append special columns (צ. יומית, ק. חצות, התראה) from the old static config
  const connectedColumns = useMemo(() => {
    const dynamic = buildConsumerColumns({
      rows: connectedRows,
      resourceNames: connectedResourceNames,
      resourceHeaders: connectedResourceHeaders,
      langKey,
    })

    // Remove dailyUsageData chart column — we replace it with text-based צ. יומית / ק. חצות
    const filtered = dynamic.filter(
      (col: any) => col.field !== 'dailyUsageData'
    )

    // Extra columns specific to connected meters table
    const extraColumns: any[] = [
      {
        field: 'dailyUsageData',
        headerName: 'צ. יומית',
        flex: 1,
        minWidth: 100,
        hideable: true,
        disableAggregation: true,
        filter: { type: 'none' },
        filterable: false,
        sortable: false,
        renderCell: (params: any) => {
          const data = params.value as { value: number; date: string }[]
          if (Array.isArray(data) && data.length > 0) {
            return data[data.length - 1].value ?? '-'
          }
          return '-'
        },
      },
      {
        field: '_nightConsumption',
        headerName: 'ק. חצות',
        flex: 1,
        minWidth: 100,
        hideable: true,
        disableAggregation: true,
        filter: { type: 'none' },
        filterable: false,
        sortable: false,
        renderCell: (params: any) => {
          const data = params.row?.dailyUsageData as {
            value: number
            night?: number
            date: string
          }[]
          if (Array.isArray(data) && data.length > 0) {
            return data[data.length - 1].night ?? '-'
          }
          return '-'
        },
      },
      {
        field: 'openAlerts',
        headerName: 'התראה',
        flex: 1,
        minWidth: 100,
        hideable: true,
        disableAggregation: true,
        filter: { type: 'options' },
        filterable: true,
        renderCell: (params: any) => {
          const alerts = params.value
          if (Array.isArray(alerts) && alerts.length > 0) {
            const alertName = alerts
              .map((a: any) => a.name || 'Alert')
              .join(', ')
            return renderAlertWithMutedIndication(alertName, params.row)
          }
          return '-'
        },
      },
    ]

    // Only add extra columns that aren't already in the dynamic set
    const existingFields = new Set(filtered.map((col: any) => col.field))
    const toAppend = extraColumns.filter(col => !existingFields.has(col.field))

    return [...filtered, ...toAppend]
  }, [connectedRows, connectedResourceNames, connectedResourceHeaders, langKey])

  // The details page owns the connected-meters table, so export that table
  // rather than relying on whichever table happened to update the global store.
  const handleExcelExport = useCallback(() => {
    if (!details) return
    const exportColumns = connectedColumns.map(column => ({
      field: column.field,
      headerName: column.headerName ?? column.field,
    }))

    exportConsumerDetailsToExcel(
      consumerId ?? '',
      details,
      resourceHeaders,
      exportColumns,
      connectedRows,
      langKey,
      `consumer-${consumerId ?? 'details'}-details.xlsx`
    )
  }, [consumerId, details, resourceHeaders, connectedColumns, connectedRows, langKey])

  useEffect(() => {
    if (!setExcelExportHandler) return

    setExcelExportHandler(() => handleExcelExport)
    return () => setExcelExportHandler(null)
  }, [handleExcelExport, setExcelExportHandler])

  const handleCopy = useCallback(async () => {
    if (!details) return
    const text = buildConsumerDetailsClipboardText(
      details,
      resourceHeaders,
      langKey
    )
    if (text) {
      await navigator.clipboard.writeText(text)
    }
  }, [details, resourceHeaders, langKey])

  useEffect(() => {
    if (!setCopyHandler) return

    setCopyHandler(() => handleCopy)
    return () => setCopyHandler(null)
  }, [handleCopy, setCopyHandler])

  const handlePdfExport = useCallback(() => {
    if (!details) return
    exportConsumerDetailsToPDF(
      consumerId ?? '',
      details,
      resourceHeaders,
      connectedColumns,
      connectedRows,
      langKey,
      `consumer-${consumerId ?? 'details'}-details.pdf`
    )
  }, [consumerId, details, resourceHeaders, connectedColumns, connectedRows, langKey])

  useEffect(() => {
    if (!setPdfExportHandler) return

    setPdfExportHandler(() => handlePdfExport)
    return () => setPdfExportHandler(null)
  }, [handlePdfExport, setPdfExportHandler])

  // Save changes - connect to API
  const handleSaveChanges = async () => {
    const invalid = details ? saveFieldError(details, editedData) : null
    if (invalid) {
      setSaveError(invalid)
      return
    }
    const changes = details ? changedSaveFields(details, editedData) : {}
    if (Object.keys(changes).length === 0) {
      setSaveError(null)
      setIsEditMode(false)
      return
    }

    const accountId = localStorage.getItem('accountId')
    if (!accountId || !consumerId) {
      setSaveError('חסר מזהה חשבון או מזהה צרכן')
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      const result = await updateConsumer(accountId, consumerId, changes)

      if (result.success) {
        // Re-fetch consumer data from backend to get consistent state
        // (PUT response structure differs from GET - missing resources[], etc.)
        const refreshed = await fetchSingleConsumer(
          accountId,
          consumerId,
          langKey
        )
        if (refreshed.ok) {
          setDetails(refreshed.data)
          showConsumer?.(refreshed)
        }
        setIsEditMode(false)
      } else {
        setSaveError(
          `${CONSUMER_SAVE_FAILED} — ${describeGatewayError(
            result.error && {
              ...result.error,
              message: saveRefusalText(result.error.message),
            },
            CONSUMER_SAVE_FAILED
          )}`
        )
      }
    } catch (error) {
      console.error('[ConsumerDetails] Save error:', error)
      setSaveError(`${CONSUMER_SAVE_FAILED} — אירעה שגיאה בלתי צפויה`)
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    const fetchConsumerDetails = async () => {
      const accountId = localStorage.getItem('accountId')
      if (!accountId || !consumerId) {
        setDetails(null)
        setLoadError({
          message: accountId
            ? 'מספר המד חסר בכתובת הדף'
            : 'פרטי החשבון חסרים — התחברו מחדש',
        })
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      const result = await fetchSingleConsumer(accountId, consumerId, langKey)
      showConsumer?.(result)
      if (result.ok) {
        setDetails(result.data)
        setLoadError(null)
      } else {
        setDetails(null)
        setLoadError(result.error)
      }
      setIsLoading(false)
    }

    fetchConsumerDetails()
  }, [consumerId, langKey, reloadToken])

  // Fetch Connected Meters when details are available or pagination changes
  useEffect(() => {
    const fetchConnectedMeters = async () => {
      const accountId = localStorage.getItem('accountId')
      if (!accountId || !details?.premiseId) return

      try {
        setIsConnectedLoading(true)
        setConnectedError(null)
        // Get date range for last 3 months
        const today = new Date()
        const threeMonthsAgo = new Date()
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)

        const result = await getConnectedMeters(accountId, {
          page: connectedPage + 1, // API is 1-indexed
          size: connectedPageSize,
          premiseId: details.premiseId,
          parentId: details.parentId ?? details.premiseId,
          from: threeMonthsAgo.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0],
          langKey,
        })

        setConnectedRows(result.rows)
        setConnectedTotal(result.total)
        setConnectedResourceNames(result.resourceNames)
        setConnectedResourceHeaders(result.resourceHeaders)
        setConnectedError(result.error)
      } catch (err) {
        console.error('Failed to fetch connected meters', err)
        setConnectedError({ message: 'אירעה שגיאה בלתי צפויה' })
      } finally {
        setIsConnectedLoading(false)
      }
    }

    if (details) {
      fetchConnectedMeters()
    }
  }, [details, connectedPage, connectedPageSize])

  if (isLoading) {
    return (
      <Box
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}
      >
        {/* Edit Button Skeleton */}
        <Skeleton
          variant="rectangular"
          width={145}
          height={36}
          sx={{
            borderRadius: 1,
            mb: 1,
            bgcolor: isDarkMode
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.04)',
          }}
        />

        {/* Row 1 Skeleton: Chart */}
        <Box sx={{ display: 'flex', gap: 2, height: 330 }}>
          <Skeleton
            variant="rectangular"
            sx={{
              flex: 1,
              height: '100%',
              borderRadius: 2,
              bgcolor: isDarkMode
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            }}
          />
        </Box>

        {/* Row 2 Skeletons: Consumer | Meter | Map */}
        <Box sx={{ display: 'flex', gap: 2, height: 280 }}>
          <Skeleton
            variant="rectangular"
            sx={{
              width: '18%',
              minWidth: 180,
              height: '100%',
              borderRadius: 2,
              mt: 5,
              bgcolor: isDarkMode
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            }}
          />
          <Skeleton
            variant="rectangular"
            sx={{
              width: '22%',
              minWidth: 200,
              height: '100%',
              borderRadius: 2,
              bgcolor: isDarkMode
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            }}
          />
          <Skeleton
            variant="rectangular"
            sx={{
              flex: 1,
              height: '100%',
              borderRadius: 2,
              bgcolor: isDarkMode
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            }}
          />
        </Box>

        {/* Row 3 Skeleton: Table */}
        <Box sx={{ display: 'flex', gap: 2, minHeight: 400 }}>
          <Skeleton
            variant="rectangular"
            sx={{
              flex: 1,
              height: '100%',
              borderRadius: 2,
              bgcolor: isDarkMode
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            }}
          />
        </Box>
      </Box>
    )
  }

  // A failed request is not empty data: without this the cards render "-" for
  // every field and the page looks like a meter with no details.
  if (loadError || !details) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', p: 2 }}>
        <PageStateMessage
          tone="error"
          title={
            consumerId
              ? `טעינת פרטי המד ${consumerId} נכשלה`
              : CONSUMER_FETCH_FAILED
          }
          detail={describeGatewayError(loadError, CONSUMER_FETCH_FAILED)}
          onRetry={() => setReloadToken(token => token + 1)}
        />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
        minWidth: 0,
      }}
    >
      {/* Edit Mode Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Error message */}
        {saveError && (
          <Typography
            dir="rtl"
            sx={{ color: '#F44336', fontSize: 14, fontFamily: RUBIK }}
          >
            ⚠️ {saveError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
          {isEditMode ? (
            <>
              {/* Save Button */}
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={isSaving}
                sx={{
                  bgcolor: '#72A4EB',
                  color: '#FFFFFF',
                  borderRadius: 1,
                  py: 1,
                  px: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#72A4EB',
                    filter: 'brightness(0.9)',
                    boxShadow: 'none',
                  },
                  '&:disabled': { bgcolor: '#81C784', color: '#FFFFFF' },
                  fontWeight: 400,
                  fontSize: 14,
                  gap: 1,
                }}
              >
                {isSaving ? (
                  <>
                    <Box
                      sx={{
                        width: 17,
                        height: 17,
                        border: '2px solid white',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          to: { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                    שומר...
                  </>
                ) : (
                  <>
                    <SaveIcon sx={{ fontSize: 18 }} />
                    שמירת שינויים
                  </>
                )}
              </Button>
              {/* Cancel Button */}
              <Button
                variant="outlined"
                onClick={handleCancelClick}
                sx={{
                  bgcolor: isDarkMode ? '#374151' : '#F5F5F5',
                  color: isDarkMode ? '#FFFFFF' : '#003F61',
                  borderRadius: 1,
                  py: 1,
                  px: 2,
                  border: `1px solid ${BORDER}`,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: isDarkMode ? '#4B5563' : '#E0E0E0',
                    boxShadow: 'none',
                  },
                  fontWeight: 400,
                  fontSize: 14,
                }}
              >
                ביטול
              </Button>
            </>
          ) : (
            /* Edit Button */
            <Button
              variant="contained"
              onClick={() => setIsEditMode(true)}
              sx={{
                bgcolor: isDarkMode ? '#202B40' : '#E3F5FF',
                color: isDarkMode ? '#FFFFFF' : '#003F61',
                borderRadius: 1,
                py: 1,
                border: `1px solid ${BORDER}`,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: isDarkMode ? '#2d3d55' : '#D8EAFB',
                  boxShadow: 'none',
                },
                fontWeight: 300,
                width: 145,
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 14,
              }}
            >
              עריכת פרטים
              <img
                src={EditOutlinedIcon}
                alt="Edit"
                style={{
                  width: '17px',
                  height: '17px',
                  filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            </Button>
          )}
        </Box>
      </Box>

      <Dialog
        open={showCancelDialog}
        onClose={handleContinueEditing}
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? '#202B40' : '#FFFFFF',
            minWidth: 360,
            direction: 'rtl',
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: isDarkMode ? '#FFFFFF' : '#003F61',
            fontFamily: RUBIK,
            fontSize: 16,
            fontWeight: 400,
            pt: 3,
          }}
        >
          פעולה זו תבטל את השינויים
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography
            sx={{
              color: isDarkMode ? '#B0B0B0' : '#666666',
              fontFamily: RUBIK,
              fontSize: 14,
            }}
          >
            שביצעתם בפרטי המד והצרכן
          </Typography>
          <Typography
            sx={{
              color: isDarkMode ? '#B0B0B0' : '#666666',
              fontFamily: RUBIK,
              fontSize: 14,
              mt: 1,
            }}
          >
            האם לבטל?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleContinueEditing}
            sx={{
              borderColor: isDarkMode ? '#4B5563' : '#E4E7EB',
              color: isDarkMode ? '#FFFFFF' : '#003F61',
              borderRadius: 1,
              px: 3,
              fontFamily: RUBIK,
              fontSize: 14,
              '&:hover': { bgcolor: isDarkMode ? '#374151' : '#F5F5F5' },
            }}
          >
            המשך עריכה
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancel}
            sx={{
              bgcolor: '#E74C3C',
              color: '#FFFFFF',
              borderRadius: 1,
              px: 3,
              fontFamily: RUBIK,
              fontSize: 14,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#C0392B', boxShadow: 'none' },
            }}
          >
            ביטול שינויים
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main section: [Cards left] | [Chart + Map right] — matching Figma */}
      <Box sx={{ display: 'flex', gap: 2, transition: 'all 0.3s ease' }}>
        {/* Left: פרטי צרכן + פרטי מד side by side, stretching full height */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            width: '40%',
            minWidth: 320,
            alignSelf: 'flex-start',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 150 }}>
            {isEditMode && locationsError && (
              <PageStateMessage
                tone="error"
                title={LOCATIONS_FETCH_FAILED}
                detail={describeGatewayError(
                  locationsError,
                  LOCATIONS_FETCH_FAILED
                )}
                onRetry={() => setLocationsToken(token => token + 1)}
              />
            )}
            <PersonalInfoCard
              data={details}
              isEditMode={isEditMode}
              editedData={editedData}
              onFieldChange={handleFieldChange}
              resourceHeaders={resourceHeaders}
              langKey={langKey}
              locations={locations}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 150,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <MeterInfoCard
              data={details}
              isEditMode={isEditMode}
              editedData={editedData}
              onFieldChange={handleFieldChange}
              resourceHeaders={resourceHeaders}
              langKey={langKey}
            />
            <TransmitterInfoCard
              data={details}
              resourceHeaders={resourceHeaders}
              langKey={langKey}
            />
          </Box>
        </Box>

        {/* Right: Chart on top, Map below (same width) */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 300,
          }}
        >
          {/* Chart */}
          <Box
            sx={{
              opacity: isEditMode ? 0.4 : 1,
              transition: 'opacity 0.3s ease',
              pointerEvents: isEditMode ? 'none' : 'auto',
            }}
          >
            <WeeklyConsumptionChart
              data={chartData}
              title="גרף צריכה יומית בפריסה שבועית"
              dateFormatter={d => {
                const day = String(d.getDate()).padStart(2, '0')
                const month = String(d.getMonth() + 1).padStart(2, '0')
                return `${day}/${month}`
              }}
              seriesNames={{ day: 'צ. יום', night: 'צ. לילה' }}
              height={240}
            />
          </Box>

          {/* Map */}
          <Box
            sx={{
              flex: 1,
              minHeight: 280,
              display: 'flex',
              flexDirection: 'column',
              ...cardBoxStyle,
              p: 2,
              opacity: isEditMode ? 0.4 : 1,
              transition: 'opacity 0.3s ease',
              pointerEvents: isEditMode ? 'none' : 'auto',
            }}
          >
            <Typography
              sx={{
                fontFamily: RUBIK,
                fontWeight: 400,
                fontSize: 16,
                lineHeight: 1,
                textAlign: 'end',
                color: isDarkMode ? '#FFFFFF' : '#003F61',
              }}
            >
              תצוגה על גבי מפה
            </Typography>
            <Divider
              sx={{
                my: 1,
                borderColor: isDarkMode ? '#374151' : 'rgba(0, 0, 0, 0.12)',
              }}
            />
            <Box sx={{ flex: 1, minHeight: 200, position: 'relative' }}>
              {meterCoordinates ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                >
                  <MapContainer
                    coordinates={meterCoordinates}
                    interaction="view"
                    showAllMarkers={false}
                    singleMarkerData={{
                      id: details?.meterNumber ?? 0,
                      name:
                        details?.payerName ||
                        details?.resources?.find(
                          r => r.key === 'payerName' || r.key === 'payer_name'
                        )?.value ||
                        '',
                      address:
                        details?.address ||
                        details?.resources?.find(r => r.key === 'address')
                          ?.value ||
                        '',
                    }}
                  />
                </Box>
              ) : (
                <PageStateMessage
                  title="אין מיקום שמור למד זה"
                  detail="הוסיפו קואורדינטות למד כדי להציג אותו על המפה"
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Row 3: מדים קשורים Table (full width) */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          minHeight: 400,
          minWidth: 0,
          transition: 'all 0.3s ease',
        }}
      >
        {/* עצי מים + Get CR Button - HIDDEN
        <Box sx={{
          width: isEditMode ? '30%' : '18%',
          minWidth: 180,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          transition: 'all 0.3s ease',
        }}>
          <TreeWater data={details} isEditMode={isEditMode} editedData={editedData} onFieldChange={handleFieldChange} />
          <Box
            sx={{
              display: 'inline-flex',
              height: '46px',
              padding: '0 18px',
              alignItems: 'center',
              gap: '12px',
              borderRadius: '4.5px',
              border: `1px solid ${BORDER}`,
              background: isDarkMode ? '#202B40' : '#E3F5FF',
              cursor: 'pointer',
              width: 'fit-content',
            }}
          >
            <Typography sx={{ fontFamily: 'Rubik, sans-serif', color: isDarkMode ? '#FFFFFF' : '#003F61' }}>
              Get CR
            </Typography>
            <img src={InfoIcon} alt="Info" style={{ filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }} />
          </Box>
        </Box>
        */}

        {/* מדים קשורים Table */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            ...cardBoxStyle,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            opacity: isEditMode ? 0.4 : 1,
            transition: 'opacity 0.3s ease',
            pointerEvents: isEditMode ? 'none' : 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'flex-end',
              borderBottom: `1px solid ${BORDER}`,
              paddingBottom: '8px',
              marginBottom: '8px',
            }}
          >
            <StyledTypography>מדים קשורים</StyledTypography>
            <img
              src={TableConnect}
              alt="TableConnect"
              style={{
                filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
            {connectedState.kind !== 'rows' ? (
              <PageStateMessage
                tone={connectedState.kind === 'error' ? 'error' : 'info'}
                title={connectedState.title}
                detail={connectedState.detail}
              />
            ) : (
              <DataGridPremiumDemo
                columns={connectedColumns}
                hideFooter
                width="100%"
                headerPosition="bottom"
                rows={connectedRows}
                paginationMode="server"
                serverSideRowCount={connectedTotal}
                serverSidePage={connectedPage}
                pageSize={connectedPageSize}
                onPaginationChange={(newPage, newSize) => {
                  setConnectedPage(newPage)
                  setConnectedPageSize(newSize)
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

// A meter link inside the page keeps this route mounted. Keying by meter starts the next
// meter from the loading skeleton instead of one frame of the last meter's cards.
const ConsumerDetails: React.FC = () => (
  <ConsumerDetailsOfMeter key={useParams().consumerId} />
)

export default ConsumerDetails
